import {
  assertEnv,
  getAffiliateRefIdFromSearchString,
} from "@homeinstead/shared";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { getPageCategoryFromPath } from "../utils/get-page-category-from-path";
import { getUserAgentDataFallback } from "../utils/get-user-agent-data-fallback";
import { getVisitorIdFromDocumentCookie } from "../visitor-id";
import {
  flushAnalyticsEventQueue,
  markAnalyticsInitFailed,
  markAnalyticsReadyForEvents,
} from "./analytics-event-queue";
import { getEvaluatedFlagEventProperties } from "./evaluated-flag-event-properties";
import {
  isOsanoExpectedOrPresent,
  isOsanoSegmentBridgeReady,
  waitForOsanoConsentExpectation,
  waitForOsanoSegmentBridge,
} from "./wait-for-osano-segment-bridge";

const AFFILIATE_REF_ID_TRAIT_KEY = "affiliate_ref_id";

let analytics: AnalyticsBrowser | undefined;
let sourceMiddlewareAttached = false;

function addAnalyticsSourceMiddleware(
  analyticsInstance: AnalyticsBrowser,
): void {
  if (sourceMiddlewareAttached) {
    return;
  }

  sourceMiddlewareAttached = true;

  analyticsInstance.addSourceMiddleware(({ payload, next }) => {
    const { type } = payload.obj;

    const affiliateRefId = getAffiliateRefIdFromSearchString(
      window.location.search,
    );

    if (type && ["track", "page"].includes(type)) {
      payload.obj.context = payload.obj.context || {};

      payload.obj.context.website_platform = "nextjs";

      if (affiliateRefId) {
        payload.obj.context.traits = {
          ...(payload.obj.context.traits || {}),
          [AFFILIATE_REF_ID_TRAIT_KEY]: affiliateRefId,
        };
      }

      let derivedProperties: Record<string, unknown> = {
        page_path: window.location.pathname,
        page_url: window.location.href,
      };

      const flagProperties = getEvaluatedFlagEventProperties();
      if (Object.keys(flagProperties).length) {
        derivedProperties = {
          ...derivedProperties,
          ...flagProperties,
        };
      }

      const pageCategory = getPageCategoryFromPath(window.location.pathname);
      if (pageCategory !== undefined) {
        derivedProperties = {
          ...derivedProperties,
          page_category: pageCategory,
        };
      }

      if (Object.keys(derivedProperties).length) {
        payload.obj.properties = {
          ...derivedProperties,
          ...(payload.obj.properties || {}),
        };
      }

      // Add userAgentData fallback for browsers without native Client Hints API
      // @note Segment handles this automatically for Chromium browsers.
      // @see
      //  - https://developer.mozilla.org/en-US/docs/Web/API/User-Agent_Client_Hints_API#browser_compatibility
      //  - https://www.twilio.com/docs/segment/connections/spec/common#context
      if (!navigator.userAgentData) {
        payload.obj.context.userAgentData = getUserAgentDataFallback();
      }
    }

    if (type === "identify") {
      if (affiliateRefId) {
        payload.obj.traits = {
          ...(payload.obj.traits || {}),
          [AFFILIATE_REF_ID_TRAIT_KEY]: affiliateRefId,
        };
      }
    }

    next(payload);
  });
}

async function triggerSegmentLoad(
  analyticsInstance: AnalyticsBrowser,
  writeKey: string,
  errorMessage: string,
): Promise<boolean> {
  try {
    analyticsInstance.load({ writeKey });
    await analyticsInstance.ready();
    return true;
  } catch (error: unknown) {
    console.error(errorMessage, error);
    return false;
  }
}

async function finishSegmentInitialization(
  analyticsBrowser: AnalyticsBrowser,
  writeKey: string,
): Promise<void> {
  try {
    let analyticsInstance = analyticsBrowser;

    // Client Osano loaders register asynchronously via their module/chunk;
    // wait briefly before choosing the ungated Segment path.
    await waitForOsanoConsentExpectation();

    if (!isOsanoExpectedOrPresent()) {
      addAnalyticsSourceMiddleware(analyticsInstance);
      analytics = analyticsInstance;

      const loaded = await triggerSegmentLoad(
        analyticsInstance,
        writeKey,
        "Segment analytics failed to load.",
      );

      if (!loaded) {
        markAnalyticsInitFailed("Segment failed to load");
        return;
      }

      await flushAnalyticsEventQueue(analyticsInstance);
      markAnalyticsReadyForEvents();
      return;
    }

    await waitForOsanoSegmentBridge();

    if (!isOsanoSegmentBridgeReady()) {
      console.error(
        "Osano withCMP unavailable at analytics init — Segment consent wrapper not exposed by the active Osano config; verify osano.js loads before osano.segment.js and the Segment integration is enabled/published for this config id.",
      );
      markAnalyticsInitFailed("Osano withCMP unavailable at analytics init");
      return;
    }

    analyticsInstance = window.withCMP!(analyticsBrowser);
    addAnalyticsSourceMiddleware(analyticsInstance);
    analytics = analyticsInstance;

    const loaded = await triggerSegmentLoad(
      analyticsInstance,
      writeKey,
      "Segment analytics failed to load through Osano withCMP.",
    );

    if (!loaded) {
      markAnalyticsInitFailed("Segment failed to load through Osano withCMP");
      return;
    }

    await flushAnalyticsEventQueue(analyticsInstance);
    markAnalyticsReadyForEvents();
  } catch (error: unknown) {
    console.error("Segment analytics initialization failed.", error);
    markAnalyticsInitFailed("Segment analytics initialization threw");
  }
}

/**
 * Initialize Analytics - should only be called by core analytics modules
 * @returns AnalyticsBrowser instance or undefined if not available
 */
function initializeAnalytics(): AnalyticsBrowser | undefined {
  // Skip initialization on server for now
  if (typeof window === "undefined") {
    // AnalyticsBrowser is not available on server
    return undefined;
  }

  if (analytics) {
    return analytics;
  }

  let writeKey;

  try {
    writeKey = assertEnv(
      "NEXT_PUBLIC_SEGMENT_WRITE_KEY",
      process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
    );
  } catch {
    return undefined;
  }

  const analyticsBrowser = new AnalyticsBrowser();
  analytics = analyticsBrowser;

  const visitorId = getVisitorIdFromDocumentCookie();
  if (visitorId) {
    void analyticsBrowser.setAnonymousId(visitorId);
  }

  void finishSegmentInitialization(analyticsBrowser, writeKey);

  return analyticsBrowser;
}

initializeAnalytics();

type EventType = "page" | "track" | "identify";
type GetAnalyticsInstanceParams = {
  eventType?: EventType;
};

/**
 * Get analytics instance or attempt to reinitialize if not available
 *
 * If unavailable, a console warning will be logged for {eventType}
 * @param eventType Optional event name for logging
 */
function getAnalyticsInstance({ eventType }: GetAnalyticsInstanceParams) {
  if (typeof window === "undefined") {
    return; // nothing happening on the server
  }

  const analyticsInstance = analytics ?? initializeAnalytics();

  if (analyticsInstance) {
    return analyticsInstance;
  }

  console.warn(
    `Analytics unavailable${eventType ? ` for ${eventType} event` : ""}`,
  );
}

export { getAnalyticsInstance };
